import { useEffect, useState } from 'react';
import { apiClient, Story } from '../lib/apiClient';
import SceneReview from '../components/SceneReview';

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; today: Story | null; catalog: Story[] };

/** Studio de relecture (lecture seule) : conte du jour ou conte du catalogue, scène par scène. */
export default function ReviewStudio() {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sceneIndex, setSceneIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState({ status: 'loading' });
      const [todayRes, catalogRes] = await Promise.allSettled([
        apiClient.fetchTodayStory(),
        apiClient.fetchCatalog(),
      ]);
      if (cancelled) return;
      if (todayRes.status === 'rejected' && catalogRes.status === 'rejected') {
        setState({
          status: 'error',
          message: todayRes.reason instanceof Error ? todayRes.reason.message : 'Chargement impossible',
        });
        return;
      }
      const today = todayRes.status === 'fulfilled' ? todayRes.value : null;
      const catalog = catalogRes.status === 'fulfilled' ? catalogRes.value.stories ?? [] : [];
      setState({ status: 'ready', today, catalog });
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') {
    return (
      <p role="status" className="text-slate-400 text-sm animate-pulse">
        Chargement du conte…
      </p>
    );
  }
  if (state.status === 'error') {
    return (
      <p role="alert" className="text-red-400 text-sm">
        Erreur : {state.message}
      </p>
    );
  }

  const options = [...(state.today ? [state.today] : []), ...state.catalog.filter((s) => s.id !== state.today?.id)];
  const story = options.find((s) => s.id === selectedId) ?? options[0];
  const scenes = story?.scenes ?? [];

  if (!story || scenes.length === 0) {
    return (
      <p role="status" className="text-slate-400 text-sm">
        {story ? 'Ce conte ne contient aucune scène.' : 'Aucun conte à relire.'}
      </p>
    );
  }

  const index = Math.min(sceneIndex, scenes.length - 1);
  const select = (id: string) => {
    setSelectedId(id);
    setSceneIndex(0);
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-5" aria-label="Studio de relecture">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-100">{story.title}</h2>
          <p className="text-xs text-slate-400">
            {story.publication_date} • {story.target_age_group} ans • lecture seule
          </p>
        </div>
        <label className="text-xs text-slate-400 flex items-center gap-2">
          Conte
          <select
            value={story.id}
            onChange={(e) => select(e.target.value)}
            className="bg-slate-950 border border-slate-800 text-xs text-slate-200 rounded-lg px-3 py-1.5"
          >
            {options.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title} ({s.publication_date})
              </option>
            ))}
          </select>
        </label>
      </div>

      <SceneReview scene={scenes[index]} />

      <nav className="flex items-center justify-between" aria-label="Navigation entre scènes">
        <button
          type="button"
          onClick={() => setSceneIndex(index - 1)}
          disabled={index === 0}
          className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded-md border border-slate-700"
        >
          Scène précédente
        </button>
        <span className="text-xs text-slate-400">
          Scène {index + 1} / {scenes.length}
        </span>
        <button
          type="button"
          onClick={() => setSceneIndex(index + 1)}
          disabled={index === scenes.length - 1}
          className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded-md border border-slate-700"
        >
          Scène suivante
        </button>
      </nav>
    </section>
  );
}
